import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Spinner, Table } from "reactstrap";
import moment from "moment";

import { useCompanies } from "../../../hooks/companies";
import { useDateFilter } from "../../../hooks/datefilter";
import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import MetricCard from "../../../components/MetricCard";
import MetricCardGroup from "../../../components/MetricCardGroup";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import TablePagination from "../../../components/TablePagination";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { downloadBlob } from "../../../utils/downloadBlob";
import { baixarEtapasCsv, listarEtapas } from "../../../services/despachoIndicadoresApi";

import { COLUNAS_TEMPOS, ETAPAS, filtrosDaUrl, formatarMinutos, linkLote, linkPedido, rotuloEtapa, tomEtapa } from "./regras";
import "./styles.css";

// Trackfy › Indicadores › Despacho (PO1-3495, épico PO1-3493): onde cada envio
// está entre a etiqueta e a coleta, e há quanto tempo está parado. Tela no
// padrão do Hub (PO1-2725): PageHero + MetricCardGroup (cards que filtram por
// etapa, uma por vez) + FilterBand (server) + ShellCard/TableSurface +
// TablePagination; mesmo esqueleto de Entregas Pendentes.
//
// Dados de /despacho/indicadores/etapas (backend_hub): os cards seguem período e
// transportadora, mas NÃO a busca nem a etapa, por isso o número do card bate
// com o total da lista ao clicar nele. O CSV (/etapas.csv) sai com os filtros
// atuais, sem paginação. Embarcador vai no header (services/api).

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const DIAS_PADRAO = 30;
const DATA = "YYYY-MM-DD";

const fmtNumero = (v) => (v === null || v === undefined ? "-" : Number(v).toLocaleString("pt-BR"));
const fmtDataHora = (v) => (v ? moment(v).format("DD/MM/YYYY HH:mm") : "-");

const DespachoIndicadores = ({ location }) => {
  const { empresaId, transportadoraId, arrayParams } = useCompanies();
  const { startDate, endDate, setStartDate, setEndDate } = useDateFilter();

  // Só o primeiro render lê a URL: depois a tela manda no próprio estado.
  const [filtrosIniciais] = useState(() => filtrosDaUrl(location && location.search));

  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [cards, setCards] = useState({});
  const [tempos, setTempos] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [erro, setErro] = useState("");
  const [etapa, setEtapa] = useState(filtrosIniciais.etapa);
  const [buscaRascunho, setBuscaRascunho] = useState("");
  const [busca, setBusca] = useState("");
  // Transportadora vinda pelo link: vale enquanto o filtro global estiver vazio
  // (o filtro do cabeçalho, quando escolhido, manda).
  const [transportadoraLink, setTransportadoraLink] = useState(filtrosIniciais.transportadora);

  // O período da URL (drill-down do dashboard) SEMEIA o filtro global em vez
  // de competir com ele: o botão "Período" da navbar continua respondendo.
  useEffect(() => {
    if (filtrosIniciais.de && filtrosIniciais.ate) {
      setStartDate(moment(filtrosIniciais.de));
      setEndDate(moment(filtrosIniciais.ate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fora do dashboard o filtro global costuma vir vazio; sem janela padrão a
  // consulta varreria todos os envios do embarcador.
  // Memo pelas STRINGS do período: semear a mesma data (outro objeto moment)
  // não pode virar consulta nova.
  const de = (startDate ? moment(startDate) : moment().subtract(DIAS_PADRAO, "days")).format(DATA);
  const ate = (endDate ? moment(endDate) : moment()).format(DATA);
  const periodo = useMemo(() => ({ de, ate }), [de, ate]);

  // Com período na URL, a primeira consulta espera o filtro global ser
  // semeado; senão sai uma consulta com os 30 dias padrão antes da certa.
  const aguardandoPeriodoDaUrl = useRef(Boolean(filtrosIniciais.de && filtrosIniciais.ate));

  const transportadoras = useMemo(() => {
    if (arrayParams.transportadoras) return arrayParams.transportadoras;
    return transportadoraLink ? [transportadoraLink] : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transportadoraId, transportadoraLink]);

  const paramsRecorte = useMemo(() => {
    const params = { ...periodo };
    if (transportadoras) params.transportadoras = transportadoras;
    return params;
  }, [periodo, transportadoras]);

  const paramsFiltro = useMemo(() => {
    const params = { ...paramsRecorte };
    if (etapa) params.etapa = etapa;
    if (busca) params.busca = busca;
    return params;
  }, [paramsRecorte, etapa, busca]);

  // Trocar filtro rápido dispara duas consultas; só a ÚLTIMA pode escrever no
  // estado, senão a resposta atrasada sobrescreve a atual.
  const versaoConsulta = useRef(0);
  const carregar = useCallback(async () => {
    versaoConsulta.current += 1;
    const versao = versaoConsulta.current;
    const atual = () => versao === versaoConsulta.current;
    setLoading(true);
    setErro("");
    try {
      const resposta = await listarEtapas({ ...paramsFiltro, pagina, tamanhoPagina });
      if (!atual()) return;
      setItens(resposta.itens);
      setTotal(resposta.total);
      setCards(resposta.cards);
      setTempos(resposta.tempos);
    } catch (_) {
      if (!atual()) return;
      setItens([]);
      setTotal(0);
      setErro("Não conseguimos carregar os indicadores de despacho agora.");
    } finally {
      if (atual()) setLoading(false);
    }
  }, [paramsFiltro, pagina, tamanhoPagina]);

  // Qualquer troca de recorte volta para a página 1 (senão a paginação aponta
  // para um offset que não existe mais). Fora da página 1, só volta a página e
  // deixa o render seguinte consultar: uma requisição por troca, não duas.
  const ultimoRecorte = useRef(null);
  useEffect(() => {
    if (aguardandoPeriodoDaUrl.current) {
      if (periodo.de !== filtrosIniciais.de || periodo.ate !== filtrosIniciais.ate) return;
      aguardandoPeriodoDaUrl.current = false;
    }
    const recorte = JSON.stringify([empresaId, paramsFiltro]);
    const mudou = ultimoRecorte.current !== null && ultimoRecorte.current !== recorte;
    ultimoRecorte.current = recorte;
    if (mudou && pagina !== 1) {
      setPagina(1);
      return;
    }
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregar, empresaId]);

  // Uma etapa por vez: clicar no card ativo desliga o filtro.
  const alternarEtapa = useCallback((valor) => {
    setEtapa((atual) => (atual === valor ? null : valor));
  }, []);

  const limpar = useCallback(() => {
    setBuscaRascunho("");
    setBusca("");
    setEtapa(null);
    setTransportadoraLink(null);
  }, []);

  const exportar = useCallback(async () => {
    setExportando(true);
    try {
      const { conteudo, nome } = await baixarEtapasCsv(paramsFiltro);
      downloadBlob(conteudo, nome, "text/csv");
    } catch (_) {
      setErro("Não conseguimos gerar o CSV agora.");
    } finally {
      setExportando(false);
    }
  }, [paramsFiltro]);

  const filtrosAtivos = (etapa ? 1 : 0) + (busca ? 1 : 0) + (transportadoraLink && !arrayParams.transportadoras ? 1 : 0);
  const periodoLegivel = `${moment(periodo.de).format("DD/MM/YYYY")} a ${moment(periodo.ate).format("DD/MM/YYYY")}`;

  return (
    <div className="content despacho-indicadores">
      <PageHero
        eyebrow="Trackfy · Indicadores"
        title="Despacho"
        description="Onde cada envio está entre a etiqueta e a coleta, e há quanto tempo está parado."
        actions={
          <>
            <PageHeroRefreshButton onClick={carregar} loading={loading} />
            <PageHeroExportButton onClick={exportar} loading={exportando} disabled={loading} />
          </>
        }
      />

      {/* 7 etapas: tile menor para caberem numa linha em tela larga (QA 17/10). */}
      <MetricCardGroup
        minTileWidth={150}
        title="Envios por etapa"
        description="Clique num indicador para filtrar a lista abaixo."
        action={<span className="despacho-indicadores__contagem">NFs emitidas de {periodoLegivel}</span>}
      >
        {ETAPAS.map((item) => (
          <MetricCard
            key={item.valor}
            label={item.rotulo}
            value={fmtNumero(cards[item.valor] || 0)}
            helper={item.ajuda}
            iconClassName={item.icone}
            tone={item.tom}
            onClick={() => alternarEtapa(item.valor)}
            active={etapa === item.valor}
          />
        ))}
      </MetricCardGroup>

      <FilterBand
        mode="server"
        searchLabel="Buscar envio"
        searchPlaceholder="Nota fiscal, pedido ou destinatário"
        searchHint="A busca vale sobre todo o período, não só sobre a página aberta."
        searchValue={buscaRascunho}
        onSearchChange={setBuscaRascunho}
        onSearchSubmit={setBusca}
        activeFiltersCount={filtrosAtivos}
        extra={
          filtrosAtivos ? (
            <Button size="sm" color="default" outline onClick={limpar} disabled={loading}>
              Limpar filtros
            </Button>
          ) : null
        }
      />

      <ShellCard title="Envios" description={`${fmtNumero(total)} envio(s) no recorte atual${etapa ? ` · ${rotuloEtapa(etapa)}` : ""}`}>
        <TableSurface>
          {erro ? <div className="despacho-indicadores__estado despacho-indicadores__estado--erro">{erro}</div> : null}

          {!erro && loading ? (
            <div className="despacho-indicadores__estado">
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> Consultando envios...
            </div>
          ) : null}

          {!erro && !loading && itens.length === 0 ? <div className="despacho-indicadores__estado">Nenhum envio para os filtros.</div> : null}

          {!erro && !loading && itens.length > 0 ? (
            <Table className="despacho-indicadores__tabela hub-thead-accent" size="sm" responsive hover>
              <thead className="text-primary">
                <tr>
                  <th>Nota fiscal</th>
                  <th>Pedido</th>
                  <th>Destinatário</th>
                  <th>Transportadora</th>
                  <th>Etapa</th>
                  <th className="is-number">Tempo parado</th>
                  <th>Motivo</th>
                  <th aria-label="Atalhos" />
                </tr>
              </thead>
              <tbody>
                {itens.map((e) => {
                  const motivo = e.erro || e.erroColeta || "";
                  return (
                    <tr key={e.idEnvio}>
                      <td>
                        <strong>{e.numero || "-"}</strong>
                        <small>{e.serie ? `Série ${e.serie}` : "Sem série"}</small>
                      </td>
                      <td title={e.pedido || ""}>{e.pedido || "-"}</td>
                      <td>
                        <strong title={e.destinatario || ""}>{e.destinatario || "-"}</strong>
                        <small>{[e.cidade, e.uf].filter(Boolean).join("/") || "-"}</small>
                      </td>
                      <td>
                        <strong title={e.transportadora || ""}>{e.transportadora || "-"}</strong>
                        <small>{e.servico || "-"}</small>
                      </td>
                      <td>
                        <span className={`despacho-indicadores__etapa despacho-indicadores__etapa--${tomEtapa(e.etapa)}`}>{rotuloEtapa(e.etapa)}</span>
                      </td>
                      {/* Parado desde a última data conhecida da etapa (minutosParado). */}
                      <td className="is-number" title={`Último evento: ${fmtDataHora(e.datColeta || e.datDespacho || e.datFechamento || e.datEntradaLote || e.datEmissao || e.datEnvio)}`}>
                        {formatarMinutos(e.minutosParado)}
                      </td>
                      <td title={motivo}>{motivo ? <small className="despacho-indicadores__motivo">{motivo}</small> : <span className="despacho-indicadores__muted">-</span>}</td>
                      <td>
                        <div className="despacho-indicadores__atalhos">
                          {e.pedido ? (
                            <Link to={linkPedido(e.pedido)} className="despacho-indicadores__atalho" title="Abrir em Pedidos">
                              Pedido
                            </Link>
                          ) : null}
                          {e.idLote ? (
                            <Link to={linkLote(e.idLote)} className="despacho-indicadores__atalho" title={e.codigoLote ? `Abrir lote ${e.codigoLote}` : "Abrir lote"}>
                              Lote
                            </Link>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : null}

          <TablePagination
            page={pagina}
            pageSize={tamanhoPagina}
            total={total}
            pageSizes={PAGE_SIZES}
            itemLabel="envio(s)"
            disabled={loading}
            onPageChange={setPagina}
            onPageSizeChange={(tamanho) => {
              setTamanhoPagina(tamanho);
              setPagina(1);
            }}
          />
        </TableSurface>
      </ShellCard>

      {/* Médias por transportadora no recorte (período + transportadora), em
          minutos; "–" quando a etapa não aconteceu para nenhum envio. */}
      <ShellCard title="Tempo médio por etapa" description="Média, por transportadora, do tempo entre uma etapa e a seguinte no período.">
        <TableSurface>
          {!loading && tempos.length === 0 ? <div className="despacho-indicadores__estado">Sem tempos no recorte.</div> : null}
          {tempos.length > 0 ? (
            <Table className="despacho-indicadores__tempos hub-thead-accent" size="sm" responsive>
              <thead className="text-primary">
                <tr>
                  <th>Transportadora</th>
                  {COLUNAS_TEMPOS.map((c) => (
                    <th key={c.chave} className="is-number">
                      {c.rotulo}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tempos.map((t) => (
                  <tr key={t.idTransportadora || t.nome}>
                    <td>
                      <strong>{t.nome || "-"}</strong>
                    </td>
                    {COLUNAS_TEMPOS.map((c) => (
                      <td key={c.chave} className="is-number">
                        {formatarMinutos(t[c.chave])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </TableSurface>
      </ShellCard>
    </div>
  );
};

export default DespachoIndicadores;
